// src/lib/black-friday-search-term-comparison.ts
import { BFSearchTermData, BFPMaxCategoryData, BFSearchTermComparison } from "@/types/black-friday";

/**
 * Combined search term type for unified comparison (includes both search terms and PMax categories)
 */
type CombinedSearchTerm = {
  term: string; // searchTerm or categoryLabel
  impressions: number;
  cost: number;
  conv: number;
  convValue: number;
  type: 'search' | 'pmax'; // Track the source type
  hasCostData: boolean; // True if any search/shopping data is present
};

/**
 * Convert search term data to combined format
 */
function toCombinedFormat(data: BFSearchTermData[]): CombinedSearchTerm[] {
  return data.map(item => ({
    term: item.searchTerm,
    impressions: item.impressions,
    cost: item.cost,
    conv: item.conv,
    convValue: item.convValue,
    type: 'search' as const,
    hasCostData: true, // Search/Shopping campaigns have cost data
  }));
}

/**
 * Convert PMax category data to combined format
 */
function pMaxToCombinedFormat(data: BFPMaxCategoryData[]): CombinedSearchTerm[] {
  return data.map(item => ({
    term: item.categoryLabel,
    impressions: item.impressions,
    cost: 0, // PMax categories don't have cost in the data
    conv: item.conv,
    convValue: item.convValue,
    type: 'pmax' as const,
    hasCostData: false, // PMax categories don't have cost data
  }));
}

/**
 * Create search term comparison data by matching terms across years
 * @param searchTerms25 - 2025 search term data
 * @param pMaxCategories25 - 2025 PMax category data
 * @param searchTermsComp - Comparison year search term data
 * @param pMaxCategoriesComp - Comparison year PMax category data
 * @returns Array of search term comparisons
 */
export function createSearchTermComparison(
  searchTerms25: BFSearchTermData[],
  pMaxCategories25: BFPMaxCategoryData[],
  searchTermsComp: BFSearchTermData[],
  pMaxCategoriesComp: BFPMaxCategoryData[]
): BFSearchTermComparison[] {
  // Combine search terms and PMax categories
  const combined25 = [
    ...toCombinedFormat(searchTerms25),
    ...pMaxToCombinedFormat(pMaxCategories25)
  ];

  const combinedComp = [
    ...toCombinedFormat(searchTermsComp),
    ...pMaxToCombinedFormat(pMaxCategoriesComp)
  ];

  // Create lookup map for comparison year terms
  const comparisonMap = new Map<string, CombinedSearchTerm>();
  combinedComp.forEach(term => {
    const key = term.term.toLowerCase().trim();
    if (key && key !== '') {
      // If term already exists, aggregate the metrics
      const existing = comparisonMap.get(key);
      if (existing) {
        existing.impressions += term.impressions;
        existing.cost += term.cost;
        existing.conv += term.conv;
        existing.convValue += term.convValue;
        // If ANY source has cost data, mark as having cost data
        existing.hasCostData = existing.hasCostData || term.hasCostData;
      } else {
        comparisonMap.set(key, { ...term });
      }
    }
  });

  // Create comparison array
  const comparisons: BFSearchTermComparison[] = [];
  const processed25Map = new Map<string, CombinedSearchTerm>();

  // Aggregate 2025 terms
  combined25.forEach(term => {
    const key = term.term.toLowerCase().trim();
    if (key && key !== '') {
      const existing = processed25Map.get(key);
      if (existing) {
        existing.impressions += term.impressions;
        existing.cost += term.cost;
        existing.conv += term.conv;
        existing.convValue += term.convValue;
        // If ANY source has cost data, mark as having cost data
        existing.hasCostData = existing.hasCostData || term.hasCostData;
      } else {
        processed25Map.set(key, { ...term });
      }
    }
  });

  // Process 2025 terms
  processed25Map.forEach((term25, key) => {
    const compTerm = comparisonMap.get(key);

    const comparison: BFSearchTermComparison = {
      searchTerm: term25.term,

      // 2025 data
      impressions25: term25.impressions,
      cost25: term25.cost,
      conv25: term25.conv,
      convValue25: term25.convValue,

      // Comparison data
      impressionsComp: compTerm?.impressions || 0,
      costComp: compTerm?.cost || 0,
      convComp: compTerm?.conv || 0,
      convValueComp: compTerm?.convValue || 0,

      // Calculate changes
      impressionsChange: term25.impressions - (compTerm?.impressions || 0),
      impressionsChangePct: calculatePercentageChange(term25.impressions, compTerm?.impressions || 0),
      costChange: term25.cost - (compTerm?.cost || 0),
      costChangePct: calculatePercentageChange(term25.cost, compTerm?.cost || 0),
      convChange: term25.conv - (compTerm?.conv || 0),
      convChangePct: calculatePercentageChange(term25.conv, compTerm?.conv || 0),
      convValueChange: term25.convValue - (compTerm?.convValue || 0),
      convValueChangePct: calculatePercentageChange(term25.convValue, compTerm?.convValue || 0),

      // Flags
      hasCurrentData: true,
      hasComparisonData: !!compTerm,
      hasCostData25: term25.hasCostData,
      hasCostDataComp: compTerm?.hasCostData || false,
    };

    comparisons.push(comparison);

    // Remove from comparison map to track processed terms
    if (compTerm) {
      comparisonMap.delete(key);
    }
  });

  // Add terms that exist in comparison year but not in 2025
  comparisonMap.forEach(compTerm => {
    const comparison: BFSearchTermComparison = {
      searchTerm: compTerm.term,

      // No 2025 data
      impressions25: 0,
      cost25: 0,
      conv25: 0,
      convValue25: 0,

      // Comparison data
      impressionsComp: compTerm.impressions,
      costComp: compTerm.cost,
      convComp: compTerm.conv,
      convValueComp: compTerm.convValue,

      // Calculate changes (negative since no current data)
      impressionsChange: -compTerm.impressions,
      impressionsChangePct: -100,
      costChange: -compTerm.cost,
      costChangePct: -100,
      convChange: -compTerm.conv,
      convChangePct: -100,
      convValueChange: -compTerm.convValue,
      convValueChangePct: -100,

      // Flags
      hasCurrentData: false,
      hasComparisonData: true,
      hasCostData25: false, // No 2025 data, so no cost data
      hasCostDataComp: compTerm.hasCostData,
    };

    comparisons.push(comparison);
  });

  return comparisons;
}

/**
 * Calculate percentage change between two values
 */
function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return ((current - previous) / previous) * 100;
}

/**
 * Filter search term comparisons based on data availability
 */
export function filterSearchTermComparisons(
  comparisons: BFSearchTermComparison[],
  filterType: 'all' | 'new' | 'existing' | 'missing'
): BFSearchTermComparison[] {
  switch (filterType) {
    case 'new':
      // Only terms that exist in 2025 but not in comparison year
      return comparisons.filter(c => c.hasCurrentData && !c.hasComparisonData);
    case 'existing':
      // Only terms that exist in both years
      return comparisons.filter(c => c.hasCurrentData && c.hasComparisonData);
    case 'missing':
      // Only terms that existed in comparison year but not in 2025
      return comparisons.filter(c => !c.hasCurrentData && c.hasComparisonData);
    case 'all':
    default:
      return comparisons;
  }
}

/**
 * Sort search term comparisons by a specified column
 */
export function sortSearchTermComparisons(
  comparisons: BFSearchTermComparison[],
  sortColumn: string,
  direction: 'asc' | 'desc'
): BFSearchTermComparison[] {
  const sorted = [...comparisons].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    // Get values based on sort column
    switch (sortColumn) {
      case 'searchTerm':
        aVal = a.searchTerm.toLowerCase();
        bVal = b.searchTerm.toLowerCase();
        break;
      case 'impressions25':
        aVal = a.impressions25;
        bVal = b.impressions25;
        break;
      case 'cost25':
        aVal = a.cost25;
        bVal = b.cost25;
        break;
      case 'conv25':
        aVal = a.conv25;
        bVal = b.conv25;
        break;
      case 'convValue25':
        aVal = a.convValue25;
        bVal = b.convValue25;
        break;
      case 'impressionsComp':
        aVal = a.impressionsComp;
        bVal = b.impressionsComp;
        break;
      case 'costComp':
        aVal = a.costComp;
        bVal = b.costComp;
        break;
      case 'convComp':
        aVal = a.convComp;
        bVal = b.convComp;
        break;
      case 'convValueComp':
        aVal = a.convValueComp;
        bVal = b.convValueComp;
        break;
      case 'impressionsChange':
        aVal = a.impressionsChange;
        bVal = b.impressionsChange;
        break;
      case 'costChange':
        aVal = a.costChange;
        bVal = b.costChange;
        break;
      case 'convChange':
        aVal = a.convChange;
        bVal = b.convChange;
        break;
      case 'convValueChange':
        aVal = a.convValueChange;
        bVal = b.convValueChange;
        break;
      case 'impressionsChangePct':
        aVal = a.impressionsChangePct;
        bVal = b.impressionsChangePct;
        break;
      case 'costChangePct':
        aVal = a.costChangePct;
        bVal = b.costChangePct;
        break;
      case 'convChangePct':
        aVal = a.convChangePct;
        bVal = b.convChangePct;
        break;
      case 'convValueChangePct':
        aVal = a.convValueChangePct;
        bVal = b.convValueChangePct;
        break;
      default:
        // Default to cost25
        aVal = a.cost25;
        bVal = b.cost25;
    }

    // Compare values
    if (typeof aVal === 'string') {
      return direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    } else {
      return direction === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }
  });

  return sorted;
}
